<template>
    <div class="voice-recorder" :class="{ compact }">
        <button
            :class="['voice-btn', { 
                'recording': isRecording, 
                'connecting': isConnecting,
                'connecting-websocket': isConnectingWebSocket,
                'pressed': isButtonPressed
            }]"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseLeave"
            @touchstart.prevent="handleTouchStart"
            @touchend.prevent="handleTouchEnd"
            @touchcancel.prevent="handleTouchCancel"
            :disabled="isConnecting || isConnectingWebSocket"
            :title="getButtonText()"
        >
            <template v-if="!compact">
                <img 
                    :src="microphoneIcon" 
                    alt="语音输入" 
                    class="microphone-icon"
                />
            </template>
            <template v-else>
                <!-- 紧凑模式：显示录音状态 -->
                <div v-if="isButtonPressed || isRecording || isConnectingWebSocket" class="compact-recording-state">
                    <img 
                        :src="microphoneIcon" 
                        alt="录音" 
                        class="compact-mic-icon"
                    />
                    <span class="compact-text">
                        <span v-if="isConnectingWebSocket">连接中…</span>
                        <span v-else-if="isRecording || isButtonPressed">松开结束</span>
                    </span>
                    <span v-if="isRecording" class="compact-timer">{{ formatTime(recordingTime) }}</span>
                </div>
                <!-- 默认状态：显示"按住说话" -->
                <span v-else class="voice-label">{{ displayText }}</span>
            </template>
        </button>
        
        <!-- 连接进度提示（仅在非紧凑模式显示） -->
        <div v-if="!compact && isConnectingWebSocket" class="connection-progress">
            <div class="progress-bar">
                <div class="progress-fill" :style="{ width: connectionProgress + '%' }"></div>
            </div>
            <div class="progress-text">连接中...</div>
        </div>
    </div>
    
    <!-- 紧凑模式：在聊天内容区域显示提示 -->
    <Teleport to="body">
        <div
            v-if="compact && (isButtonPressed || isRecording || isConnectingWebSocket || isConnecting)"
            class="compact-voice-hint"
        >
            <div class="compact-hint-box" :class="{ recording: isRecording, connecting: isConnectingWebSocket || isConnecting }">
                <img :src="microphoneIcon" alt="录音" class="compact-hint-mic" :class="{ pulse: isConnectingWebSocket || isConnecting }" />
                <div class="compact-hint-text">
                    <div v-if="isConnectingWebSocket" class="hint-connecting">
                        <div class="hint-title">正在连接</div>
                        <div class="hint-subtitle">请稍候，连接成功后即可说话</div>
                        <div class="hint-dots">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                    <div v-else-if="isConnecting" class="hint-connecting">
                        <div class="hint-title">准备录音</div>
                        <div class="hint-subtitle">正在启动录音器...</div>
                        <div class="hint-dots">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                    <div v-else-if="isRecording" class="hint-recording">
                        <div class="hint-title">正在录音</div>
                        <div class="hint-subtitle">松开结束 {{ formatTime(recordingTime) }}</div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
    
    <!-- 居中录音提示（仿微信），仅在非紧凑模式显示 -->
    <div
        v-if="!compact && (isButtonPressed || isRecording || isConnectingWebSocket || isConnecting)"
        class="record-overlay"
    >
        <div class="record-overlay-box" :class="{ recording: isRecording, connecting: isConnectingWebSocket || isConnecting }">
            <img :src="microphoneIcon" alt="录音" class="overlay-mic" :class="{ pulse: isConnectingWebSocket || isConnecting }" />
            <div class="overlay-text">
                <div v-if="isConnectingWebSocket" class="connecting-status">
                    <div class="connecting-title">正在连接</div>
                    <div class="connecting-subtitle">请稍候，连接成功后即可说话</div>
                    <div class="connecting-progress">
                        <div class="progress-dots">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                </div>
                <div v-else-if="isConnecting" class="connecting-status">
                    <div class="connecting-title">准备录音</div>
                    <div class="connecting-subtitle">正在启动录音器...</div>
                    <div class="connecting-progress">
                        <div class="progress-dots">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                </div>
                <div v-else-if="isRecording" class="recording-status">
                    <div class="recording-title">正在录音</div>
                    <div class="recording-subtitle">松开结束 {{ formatTime(recordingTime) }}</div>
                </div>
                <div v-else class="ready-status">
                    <div class="ready-title">已就绪</div>
                    <div class="ready-subtitle">可以开始说话</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'VoiceRecorder',
    props: {
        // 接收识别结果回调
        onResult: {
            type: Function,
            default: () => {}
        },
        // 紧凑样式，用于输入栏内的“按住说话”
        compact: {
            type: Boolean,
            default: false
        },
        // 录音状态回调：start/progress/stop/cancel
        onState: {
            type: Function,
            default: null
        }
    },
    data() {
        return {
            isRecording: false,
            isConnecting: false,
            isConnectingWebSocket: false,
            recordingTime: 0,
            recorder: null,
            speechService: null,
            timeInterval: null,
            backupTimerId: null, // 备用计时器的 setTimeout ID
            isWebSocketConnected: false,
            isButtonPressed: false,
            connectionProgress: 0,
            recognizedText: '', // 存储识别到的文本
            isRecognitionComplete: false, // 标记识别是否完成
            audioChunks: [], // 存储音频数据块
            audioDuration: 0, // 音频时长（秒）
            audioBlob: null, // 最终的音频Blob
            pendingAudioData: null, // 手机端：存储待确认的录音数据
            pressStartTime: 0, // 按住开始的时间戳
            minHoldDuration: 200 // 最小按住时间（毫秒），防止误触，降低到200ms
        }
    },
    computed: {
        microphoneIcon() {
            if (this.isRecording || this.isButtonPressed) {
                return require('@/assets/images/svg/microphoneClick.svg')
            } else {
                return require('@/assets/images/svg/microphone.svg')
            }
        },
        displayText() {
            if (this.isConnectingWebSocket) return '连接中…'
            if (this.isRecording) return `松开结束 ${this.formatTime(this.recordingTime)}`
            if (this.isButtonPressed) return '准备录音…'
            return '按住说话'
        }
    },
    async mounted() {
        // 等待RecorderManager加载
        await this.waitForRecorderManager()
        this.initServices()
    },
    beforeUnmount() {
        this.cleanup()
    },
    methods: {
        formatTime(sec) {
            const m = String(Math.floor((sec || 0) / 60)).padStart(2, '0')
            const s = String((sec || 0) % 60).padStart(2, '0')
            return `${m}:${s}`
        },
        // 等待RecorderManager加载
        waitForRecorderManager() {
            return new Promise((resolve) => {
                const check = () => {
                    if (window.RecorderManager) {
                        resolve()
                    } else {
                        setTimeout(check, 100)
                    }
                }
                check()
            })
        },

        initServices() {
            // 预加载SpeechService - 提高后续连接速度
            if (!this.speechService) {
                import('../services/speechService.js').then(module => {
                    this.speechService = new module.SpeechService()
                    console.log('SpeechService预加载完成')
                }).catch(error => {
                    console.error('SpeechService加载失败:', error)
                })
            }
        },

        // 连接WebSocket（按住时调用）
        async connectWebSocket() {
            if (this.isConnectingWebSocket) {
                return
            }

            // 先关闭之前的连接（如果有的话）
            if (this.speechService && this.speechService.ws) {
                console.log('关闭之前的WebSocket连接...')
                this.speechService.close()
            }

            this.isConnectingWebSocket = true
            this.isWebSocketConnected = false
            this.connectionProgress = 0

            // 连接超时机制 - 最多等待5秒
            const connectionTimeout = setTimeout(() => {
                if (this.isConnectingWebSocket) {
                    console.error('WebSocket连接超时')
                    this.isConnectingWebSocket = false
                    this.connectionProgress = 0
                    clearInterval(progressInterval)
                    this.showConnectionError('连接超时，请重试')
                }
            }, 5000)

            // 优化的连接进度动画 - 更快更真实
            const progressInterval = setInterval(() => {
                if (this.connectionProgress < 95) {
                    // 前80%快速增长，后15%缓慢增长
                    if (this.connectionProgress < 80) {
                        this.connectionProgress += Math.random() * 15 + 10
                    } else {
                        this.connectionProgress += Math.random() * 5 + 2
                    }
                }
            }, 40) // 更快的更新频率

            try {
                // 并行处理：同时等待SpeechService和开始连接
                console.log('开始建立新的WebSocket连接...')
                
                // 如果SpeechService还没准备好，先等待
                if (!this.speechService) {
                    await this.waitForSpeechService()
                }
                
                this.speechService.connectWebSocket(
                    // onMessage
                    (text, jsonData) => {
                        // 打印识别结果，但不立即发送
                        console.log('语音识别结果:', text)
                        console.log('识别数据:', jsonData)
                        this.recognizedText = text
                        this.isRecognitionComplete = true
                    },
                    // onOpen
                    () => {
                        console.log('✅ WebSocket连接成功！')
                        this.isWebSocketConnected = true
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 100
                        clearInterval(progressInterval)
                        clearTimeout(connectionTimeout) // 清除超时定时器
                        
                        // 连接成功后，检查用户是否还在按住按钮
                        if (this.isButtonPressed && !this.isRecording) {
                            console.log('用户还在按住按钮，开始启动录音器...')
                            // 连接成功后立即开始录音流程
                            this.startRecording()
                        }
                    },
                    // onError
                    (error) => {
                        console.error('WebSocket错误:', error)
                        this.isWebSocketConnected = false
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 0
                        clearInterval(progressInterval)
                        clearTimeout(connectionTimeout) // 清除超时定时器
                        this.showConnectionError('连接失败，请重试')
                    },
                    // onClose
                    (event) => {
                        console.log('WebSocket连接关闭:', event.code, event.reason)
                        this.isWebSocketConnected = false
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 0
                        clearInterval(progressInterval)
                        clearTimeout(connectionTimeout) // 清除超时定时器
                    }
                )
            } catch (error) {
                console.error('连接WebSocket失败:', error)
                this.isConnectingWebSocket = false
                this.connectionProgress = 0
                clearInterval(progressInterval)
                clearTimeout(connectionTimeout) // 清除超时定时器
                this.showConnectionError('连接失败，请重试')
            }
        },

        // 等待SpeechService初始化 - 优化等待时间
        waitForSpeechService() {
            return new Promise((resolve, reject) => {
                let attempts = 0
                const maxAttempts = 50 // 最多等待5秒
                
                const checkService = () => {
                    if (this.speechService) {
                        resolve()
                    } else if (attempts >= maxAttempts) {
                        reject(new Error('SpeechService初始化超时'))
                    } else {
                        attempts++
                        setTimeout(checkService, 50) // 更频繁的检查
                    }
                }
                checkService()
            })
        },

        // 显示连接错误
        showConnectionError(message) {
            console.error(message)
            // 可以在这里添加错误提示UI
        },

        // 鼠标按下事件
        handleMouseDown(event) {
            event.preventDefault()
            if (!this.isButtonPressed && !this.isRecording && !this.isConnectingWebSocket) {
                this.pressStartTime = Date.now() // 记录按下时间
                this.isButtonPressed = true
                // 先连接WebSocket，等待连接成功后再开始录音
                this.connectWebSocket()
                this.onState && this.onState('start')
            }
        },

        // 鼠标松开事件
        handleMouseUp(event) {
            event.preventDefault()
            
            // 计算按住时长
            const holdDuration = Date.now() - this.pressStartTime
            console.log('按住时长:', holdDuration, 'ms，当前状态 - 录音中:', this.isRecording, '连接中:', this.isConnectingWebSocket)
            
            // 松开时无论是否已开始录音，都应结束或取消
            if (this.isButtonPressed) {
                this.isButtonPressed = false
            }
            
            // 检查是否满足最小按住时间（防止误触）
            if (holdDuration < this.minHoldDuration) {
                console.log('⚠️ 按住时间太短（<200ms），取消操作')
                // 按住时间不足，取消操作
                if (this.isRecording) {
                    this.cancelRecording()
                } else if (this.isConnectingWebSocket || this.isWebSocketConnected) {
                    try { this.speechService && this.speechService.close() } catch (e) {}
                    this.isWebSocketConnected = false
                    this.isConnectingWebSocket = false
                    this.connectionProgress = 0
                    this.onState && this.onState('cancel')
                }
                return
            }
            
            // 按住时间足够长，正常处理
            if (this.isRecording) {
                // 如果正在录音，停止录音并发送
                console.log('✅ 松开按钮，停止录音并发送')
                this.stopRecording()
            } else if (this.isConnectingWebSocket) {
                // 如果还在连接中就松开，提示用户连接未完成
                console.log('⚠️ 连接还未完成，取消操作')
                try { this.speechService && this.speechService.close() } catch (e) {}
                this.isWebSocketConnected = false
                this.isConnectingWebSocket = false
                this.connectionProgress = 0
                this.onState && this.onState('cancel')
            } else {
                // 其他情况，取消连接
                console.log('⚠️ 录音未开始，取消连接')
                if (this.isWebSocketConnected) {
                    try { this.speechService && this.speechService.close() } catch (e) {}
                    this.isWebSocketConnected = false
                    this.onState && this.onState('cancel')
                }
            }
        },

        // 鼠标离开事件
        handleMouseLeave(event) {
            event.preventDefault()
            if (this.isButtonPressed) {
                this.isButtonPressed = false
            }
            if (this.isRecording) {
                this.stopRecording()
            } else {
                if (this.isConnectingWebSocket || this.isWebSocketConnected) {
                    try { this.speechService && this.speechService.close() } catch (e) {}
                    this.isWebSocketConnected = false
                    this.isConnectingWebSocket = false
                    this.connectionProgress = 0
                    this.onState && this.onState('cancel')
                }
            }
        },

        // 触摸开始事件
        handleTouchStart(event) {
            event.preventDefault()
            if (!this.isButtonPressed && !this.isRecording && !this.isConnectingWebSocket) {
                this.pressStartTime = Date.now() // 记录按下时间
                this.isButtonPressed = true
                // 先连接WebSocket，等待连接成功后再开始录音
                this.connectWebSocket()
            }
        },

        // 触摸结束事件（松开直接发送）
        handleTouchEnd(event) {
            console.log('触摸结束事件触发')
            this.handleTouchRelease(event)
        },

        // 触摸取消事件（手指移出按钮区域）
        handleTouchCancel(event) {
            console.log('触摸取消事件触发')
            this.handleTouchRelease(event)
        },

        // 统一处理触摸释放（touchend 和 touchcancel）
        handleTouchRelease(event) {
            if (event && event.preventDefault) {
                event.preventDefault()
            }
            
            // 计算按住时长
            const holdDuration = Date.now() - this.pressStartTime
            console.log('触摸按住时长:', holdDuration, 'ms，当前状态 - 录音中:', this.isRecording, '连接中:', this.isConnectingWebSocket)
            
            if (this.isButtonPressed) {
                this.isButtonPressed = false
            }
            
            // 检查是否满足最小按住时间（防止误触）
            if (holdDuration < this.minHoldDuration) {
                console.log('⚠️ 触摸时间太短（<200ms），取消操作')
                // 按住时间不足，取消操作
                if (this.isRecording) {
                    this.cancelRecording()
                } else if (this.isConnectingWebSocket || this.isWebSocketConnected) {
                    try { this.speechService && this.speechService.close() } catch (e) {}
                    this.isWebSocketConnected = false
                    this.isConnectingWebSocket = false
                    this.connectionProgress = 0
                    this.onState && this.onState('cancel')
                }
                return
            }
            
            // 按住时间足够长，正常处理
            if (this.isRecording) {
                // 如果正在录音，停止录音并发送
                console.log('✅ 触摸松开，停止录音并发送')
                this.stopRecording()
            } else if (this.isConnectingWebSocket) {
                // 如果还在连接中就松开，提示用户连接未完成
                console.log('⚠️ 连接还未完成，取消操作')
                try { this.speechService && this.speechService.close() } catch (e) {}
                this.isWebSocketConnected = false
                this.isConnectingWebSocket = false
                this.connectionProgress = 0
                this.onState && this.onState('cancel')
            } else {
                // 其他情况，取消连接
                console.log('⚠️ 录音未开始，取消连接')
                if (this.isWebSocketConnected) {
                    try { this.speechService && this.speechService.close() } catch (e) {}
                    this.isWebSocketConnected = false
                    this.onState && this.onState('cancel')
                }
            }
        },

        getButtonText() {
            if (this.isConnectingWebSocket) {
                return '连接中...'
            } else if (this.isRecording) {
                return '松开结束'
            } else {
                return '按住说话'
            }
        },

        // 立即开始录音（优化版）
        async startRecordingImmediately() {
            try {
                console.log('立即开始录音...')
                
                // 检查麦克风权限
                const hasPermission = await this.checkMicrophonePermission()
                if (!hasPermission) {
                    this.cancelRecording()
                    return
                }

                // 立即开始录音，不等待WebSocket
                this.isConnecting = true
                this.isRecording = true
                this.recordingTime = 0
                this.audioChunks = [] // 清空音频缓存
                this.onState && this.onState('recording')

                // 启动录音器
                await this.startRecorder()
                
                // 在后台连接WebSocket
                this.connectWebSocket()
            } catch (error) {
                console.error('录音启动失败:', error)
                this.cancelRecording()
            }
        },

        // 开始录音（连接成功后调用）
        async startRecording() {
            if (!this.isWebSocketConnected || this.isRecording || this.isConnectingWebSocket) {
                console.log('录音启动条件不满足:', {
                    isWebSocketConnected: this.isWebSocketConnected,
                    isRecording: this.isRecording,
                    isConnectingWebSocket: this.isConnectingWebSocket
                })
                return
            }

            try {
                console.log('✅ WebSocket已连接，开始启动录音器...')
                
                // 检查麦克风权限
                const hasPermission = await this.checkMicrophonePermission()
                if (!hasPermission) {
                    this.cancelRecording()
                    return
                }

                // 设置录音准备状态（但还不算真正开始录音）
                this.isConnecting = true
                // 注意：这里不设置 isRecording = true，等 onStart 回调中再设置
                this.recordingTime = 0

                if (this.speechService) {
                    this.speechService.reset()
                }

                // 启动录音器
                await this.startRecorder()
            } catch (error) {
                console.error('录音启动失败:', error)
                this.cancelRecording()
            }
        },

        // 检查麦克风权限
        async checkMicrophonePermission() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
                console.log('麦克风权限检查通过')
                stream.getTracks().forEach(track => track.stop())
                return true
            } catch (error) {
                console.error('麦克风权限检查失败:', error)
                this.showConnectionError('无法访问麦克风，请检查权限设置')
                return false
            }
        },

        // 启动录音器
        async startRecorder() {
            if (window.RecorderManager) {
                console.log('创建RecorderManager实例...')
                try {
                    this.recorder = new window.RecorderManager('./dist')
                    console.log('RecorderManager实例创建成功')
                } catch (error) {
                    console.error('创建RecorderManager失败:', error)
                    this.showConnectionError('录音器初始化失败')
                    return
                }

                this.recorder.onStart = () => {
                    console.log('🎙️ 录音真正开始 - onStart 回调触发')
                    // 清除备用计时器（如果存在）
                    if (this.backupTimerId) {
                        clearTimeout(this.backupTimerId)
                        this.backupTimerId = null
                    }
                    
                    // 在真正开始录音时，才设置录音状态并开始计时
                    this.isConnecting = false
                    this.isRecording = true
                    this.recordingTime = 0
                    
                    // 通知父组件录音已开始
                    this.onState && this.onState('recording')
                    
                    // 开始计时器
                    this.startTimer()
                    console.log('✅ 录音状态已设置，计时器已启动')
                }

                this.recorder.onFrameRecorded = ({ frameBuffer, isLastFrame }) => {
                    console.log('收到音频帧:', frameBuffer.byteLength, 'bytes, isLastFrame:', isLastFrame, 'WebSocket状态:', this.isWebSocketConnected)
                    
                    // 存储音频数据块（始终缓存，以备后续使用）
                    this.audioChunks.push(new Uint8Array(frameBuffer))
                    
                    // 如果WebSocket已连接，立即发送音频数据
                    if (
                        this.speechService &&
                        this.speechService.ws &&
                        this.speechService.ws.readyState === WebSocket.OPEN &&
                        typeof this.speechService.sendAudioData === 'function'
                    ) {
                        try {
                            this.speechService.sendAudioData(frameBuffer, isLastFrame)
                            console.log('音频数据已发送到WebSocket')
                        } catch (e) {
                            console.warn('音频数据发送失败', e)
                        }
                    } else {
                        // WebSocket还未连接，数据已缓存，等待连接成功后处理
                        console.log('WebSocket尚未连接，音频数据已缓存')
                    }
                }

                this.recorder.onStop = () => {
                    console.log('录音停止回调')
                    this.stopTimer()
                    this.isRecording = false
                    this.isConnecting = false
                    
                    // 处理音频数据
                    this.processAudioData()
                    
                    // 发送结束信号
                    if (
                        this.speechService &&
                        this.speechService.ws &&
                        this.speechService.ws.readyState === WebSocket.OPEN &&
                        typeof this.speechService.sendAudioData === 'function'
                    ) {
                        try {
                            this.speechService.sendAudioData(new ArrayBuffer(0), true)
                        } catch (e) {
                            console.warn('结束信号发送失败', e)
                        }
                    }
                }

                console.log('启动录音器...')
                try {
                    await this.recorder.start({
                        sampleRate: 16000,
                        frameSize: 1280
                    })
                    console.log('录音器启动完成，等待 onStart 回调...')
                    
                    // 如果 onStart 回调在短时间内没有触发，作为备用方案
                    // 等待200ms，如果 onStart 还没触发则设置状态并启动计时器
                    this.backupTimerId = setTimeout(() => {
                        if (!this.timeInterval && this.isConnecting) {
                            console.log('⚠️ onStart 回调未及时触发，使用备用方案启动录音状态')
                            this.isConnecting = false
                            this.isRecording = true
                            this.recordingTime = 0
                            this.onState && this.onState('recording')
                            this.startTimer()
                        }
                        this.backupTimerId = null
                    }, 200) // 等待200ms，如果 onStart 还没触发则启动备用方案
                } catch (error) {
                    console.error('录音器启动失败:', error)
                    this.showConnectionError('录音器启动失败，请检查麦克风权限')
                    this.stopRecording()
                }
            } else {
                console.error('RecorderManager不可用')
                this.showConnectionError('录音器不可用，请检查页面资源')
            }
        },

        // 处理音频数据
        processAudioData() {
            if (this.audioChunks.length > 0) {
                // 计算音频时长
                this.audioDuration = this.recordingTime
                
                // 合并音频数据块
                const totalLength = this.audioChunks.reduce((sum, chunk) => sum + chunk.length, 0)
                const combinedArray = new Uint8Array(totalLength)
                let offset = 0
                
                for (const chunk of this.audioChunks) {
                    combinedArray.set(chunk, offset)
                    offset += chunk.length
                }
                
                // 创建音频Blob
                this.audioBlob = new Blob([combinedArray], { type: 'audio/wav' })
                console.log('音频处理完成:', {
                    duration: this.audioDuration,
                    blobSize: this.audioBlob.size,
                    chunks: this.audioChunks.length
                })
            }
        },

        // 手机端：停止录音但不发送，等待确认
        stopRecordingForConfirm() {
            console.log('停止录音（手机端待确认）...')

            // 重置按钮状态
            this.isButtonPressed = false

            // 先停止录音器
            if (this.recorder) {
                this.recorder.stop()
                this.recorder = null
            }

            // 发送结束信号给WebSocket
            if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                try {
                    this.speechService.sendAudioData(new ArrayBuffer(0), true)
                } catch (e) {
                    console.warn('结束信号发送失败', e)
                }
                
                // 延迟处理，保存待确认的音频数据
                setTimeout(() => {
                    console.log('保存待确认的音频数据...')
                    if (this.speechService) {
                        this.speechService.close()
                    }
                    this.isWebSocketConnected = false
                    
                    // 保存待确认的音频数据，不立即发送
                    if (this.audioBlob && this.audioDuration > 0) {
                        this.pendingAudioData = {
                            audioBlob: this.audioBlob,
                            audioUrl: URL.createObjectURL(this.audioBlob),
                            duration: this.audioDuration,
                            text: this.recognizedText || ''
                        }
                        console.log('待确认的音频数据已保存:', this.pendingAudioData)
                        this.onState && this.onState('pending') // 通知父组件有待确认的录音
                    }
                }, 1000)
            }

            this.stopTimer()
            this.isRecording = false
            this.isConnecting = false
            // 不发送 'stop' 状态，保持为 'pending'
        },
        
        // 确认发送待确认的录音（手机端需要再点一次）
        confirmPendingRecording() {
            if (this.pendingAudioData) {
                console.log('确认发送录音:', this.pendingAudioData)
                this.onResult({
                    type: 'voice',
                    audioBlob: this.pendingAudioData.audioBlob,
                    audioUrl: this.pendingAudioData.audioUrl,
                    duration: this.pendingAudioData.duration,
                    text: this.pendingAudioData.text
                })
                
                // 清空待确认数据
                this.pendingAudioData = null
                this.recognizedText = ''
                this.isRecognitionComplete = false
                this.audioChunks = []
                this.audioDuration = 0
                this.audioBlob = null
                this.onState && this.onState('stop')
            }
        },

        // 取消录音（不发送）
        cancelRecording() {
            console.log('取消录音...')
            
            // 重置按钮状态
            this.isButtonPressed = false
            
            // 停止录音器
            if (this.recorder) {
                this.recorder.stop()
                this.recorder = null
            }
            
            // 关闭WebSocket
            if (this.speechService && this.speechService.ws) {
                try {
                    this.speechService.close()
                } catch (e) {
                    console.warn('关闭WebSocket失败', e)
                }
            }
            
            // 重置所有状态
            this.stopTimer()
            this.isRecording = false
            this.isConnecting = false
            this.isWebSocketConnected = false
            this.isConnectingWebSocket = false
            this.connectionProgress = 0
            this.recognizedText = ''
            this.isRecognitionComplete = false
            this.audioChunks = []
            this.audioDuration = 0
            this.audioBlob = null
            
            this.onState && this.onState('cancel')
        },

        stopRecording() {
            console.log('停止录音...')

            // 重置按钮状态
            this.isButtonPressed = false

            // 先停止录音器
            if (this.recorder) {
                this.recorder.stop()
                this.recorder = null
            }

            // 发送结束信号给WebSocket
            if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                try {
                    this.speechService.sendAudioData(new ArrayBuffer(0), true)
                } catch (e) {
                    console.warn('结束信号发送失败', e)
                }
                // 延迟关闭WebSocket连接，确保结束信号发送完成
                setTimeout(() => {
                    console.log('关闭WebSocket连接...')
                    if (this.speechService) {
                        this.speechService.close()
                    }
                    this.isWebSocketConnected = false
                    
                    // 录音结束后，发送音频数据和识别结果给父组件
                    if (this.audioBlob && this.audioDuration > 0) {
                        console.log('发送音频数据:', {
                            duration: this.audioDuration,
                            blobSize: this.audioBlob.size,
                            recognizedText: this.recognizedText
                        })
                        
                        // 创建音频URL
                        const audioUrl = URL.createObjectURL(this.audioBlob)
                        
                        this.onResult({
                            type: 'voice',
                            audioBlob: this.audioBlob,
                            audioUrl: audioUrl,
                            duration: this.audioDuration,
                            text: this.recognizedText || ''
                        })
                        
                        // 重置状态
                        this.recognizedText = ''
                        this.isRecognitionComplete = false
                        this.audioChunks = []
                        this.audioDuration = 0
                        this.audioBlob = null
                    }
                }, 1000) // 增加延迟时间，确保识别完成
            }

            this.stopTimer()
            this.isRecording = false
            this.isConnecting = false
            this.onState && this.onState('stop')
        },

        startTimer() {
            // 如果计时器已经在运行，先清除它
            if (this.timeInterval) {
                clearInterval(this.timeInterval)
            }
            this.timeInterval = setInterval(() => {
                this.recordingTime++
                this.onState && this.onState('progress', this.recordingTime)
                if (this.recordingTime >= 60) {
                    this.stopRecording()
                }
            }, 1000)
        },

        stopTimer() {
            if (this.timeInterval) {
                clearInterval(this.timeInterval)
                this.timeInterval = null
            }
            // 清除备用计时器
            if (this.backupTimerId) {
                clearTimeout(this.backupTimerId)
                this.backupTimerId = null
            }
        },

        cleanup() {
            this.stopRecording()
            // 关闭WebSocket连接
            if (this.speechService) {
                this.speechService.close()
            }
        }
    }
}
</script>

<style scoped>
.voice-recorder {
    position: relative;
    display: inline-block;
}

/* 让紧凑模式占满父容器宽度，配合按钮100%实现整条可按住 */
.voice-recorder.compact {
    display: block;
    width: 100%;
}

.voice-btn {
    width: 180px;
    height: 180px;
    border: none;
    border-radius: 50px;
    background: rgba(0, 0, 0, 0.3);
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}
.voice-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 1),
    rgba(204, 204, 204, 0.8),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.8),
    rgba(255, 255, 255, 0.9));
    border-radius: 50px;
    padding: 1px;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    z-index: -1;
}


.voice-btn.connecting-websocket .microphone-icon {
    animation: pulse-icon 1.5s infinite;
}

.voice-btn.recording .microphone-icon {
    animation: pulse-icon 0.8s infinite;
    transform: scale(1.1);
}

.voice-btn:not(:disabled):hover .microphone-icon {
    transform: scale(1.05);
}

.microphone-icon {
    width: 88px;
    height: 88px;
    transition: all 0.2s ease;
    pointer-events: none; /* 让触摸事件穿透到按钮 */
}

/* 紧凑模式（输入栏内） */
.voice-recorder.compact {
    flex: 1;
    min-width: 0;
}

.voice-recorder.compact .voice-btn {
    width: 100%;
    height: 48px;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.95);
    color: rgba(0, 0, 0, 0.85);
    border: 1.5px solid rgba(255, 255, 255, 0.8);
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.voice-recorder.compact .voice-btn::before {
    display: none;
}

.voice-recorder.compact .voice-btn:hover:not(.pressed):not(.recording):not(.connecting-websocket) {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(255, 255, 255, 0.95);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 按住/录音/连接中状态：带边框的深色背景 */
.voice-recorder.compact .voice-btn.pressed,
.voice-recorder.compact .voice-btn.recording,
.voice-recorder.compact .voice-btn.connecting-websocket {
    background: rgba(50, 50, 55, 0.85);
    backdrop-filter: blur(10px);
    color: rgba(255, 255, 255, 0.95);
    border: 1.5px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.voice-recorder.compact .voice-label {
    margin-left: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.85);
    font-weight: 500;
    pointer-events: none; /* 让触摸事件穿透到按钮 */
}

/* 紧凑模式录音状态显示 */
.compact-recording-state {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    justify-content: center;
    pointer-events: none; /* 让触摸事件穿透到按钮 */
}

/* 紧凑模式待确认状态显示 */
.compact-pending-state {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    justify-content: flex-start;
    cursor: pointer;
    transition: all 0.2s ease;
    pointer-events: none; /* 让触摸事件穿透到按钮 */
}

.compact-pending-state:hover {
    opacity: 0.8;
}

.compact-pending-state:active {
    transform: scale(0.98);
}

.compact-mic-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    filter: brightness(0) invert(1);
    pointer-events: none; /* 让触摸事件穿透到按钮 */
    opacity: 0.95;
}

.compact-text {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.95);
    font-weight: 500;
    flex: 1;
    pointer-events: none; /* 让触摸事件穿透到按钮 */
    white-space: nowrap;
}

.compact-text span {
    pointer-events: none; /* 让触摸事件穿透到按钮 */
}

.compact-timer {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
    min-width: 45px;
    text-align: right;
    pointer-events: none; /* 让触摸事件穿透到按钮 */
    letter-spacing: 0.5px;
}

/* 录音时的脉冲动画 */
.voice-recorder.compact .voice-btn.recording .compact-mic-icon {
    animation: recording-pulse 1.5s ease-in-out infinite;
}

@keyframes recording-pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.8;
        transform: scale(1.1);
    }
}

/* 紧凑模式：聊天内容区域提示框 */
.compact-voice-hint {
    position: fixed;
    left: 50%;
    bottom: 140px; /* 输入框上方 */
    transform: translateX(-50%);
    pointer-events: none;
    z-index: 9998;
    animation: slideUpFade 0.3s ease-out;
}

.compact-hint-box {
    min-width: 240px;
    max-width: 320px;
    padding: 24px 28px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(50,50,55,0.95) 70%, rgba(0,0,0,0.85));
    backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    gap: 16px;
    transition: all 0.3s ease;
}

.compact-hint-box.connecting {
    background: linear-gradient(135deg, rgba(74, 111, 165, 0.95) 70%, rgba(46, 74, 107, 0.85));
}

.compact-hint-box.recording {
    background: linear-gradient(135deg, rgba(49, 68, 91, 0.95) 80%, rgba(20, 29, 41, 0.85));
}

.compact-hint-mic {
    width: 36px;
    height: 36px;
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.3));
    flex-shrink: 0;
    transition: all 0.3s ease;
}

.compact-hint-mic.pulse {
    animation: pulse-mic 1.5s ease-in-out infinite;
}

.compact-hint-text {
    flex: 1;
    color: #fff;
}

.hint-connecting,
.hint-recording {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.hint-title {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
}

.hint-subtitle {
    font-size: 13px;
    opacity: 0.9;
    line-height: 1.4;
}

.hint-recording .hint-title {
    color: #A6F55D;
}

.hint-dots {
    display: flex;
    gap: 6px;
    margin-top: 4px;
}

.hint-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    animation: dot-pulse 1.4s ease-in-out infinite;
}

.hint-dots span:nth-child(2) {
    animation-delay: 0.2s;
}

.hint-dots span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes slideUpFade {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

/* 紧凑模式提示框响应式 */
@media (max-width: 375px) {
    .compact-voice-hint {
        bottom: 120px;
        max-width: 90%;
    }
    
    .compact-hint-box {
        min-width: 200px;
        padding: 20px 24px;
    }
    
    .hint-title {
        font-size: 16px;
    }
    
    .hint-subtitle {
        font-size: 12px;
    }
}

@media (max-height: 667px) {
    .compact-voice-hint {
        bottom: 100px;
    }
}

/* 中间提示蒙层（非紧凑模式） */
.record-overlay {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 9999;
    background: rgba(80, 80, 80, 0.05);
    animation: fadeInOverlay .2s;
}
.record-overlay-box {
    min-width: 280px;
    max-width: 360px;
    padding: 36px 32px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(50,50,55,0.95) 70%, rgba(0,0,0,0.85));
    backdrop-filter: blur(20px);
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    box-shadow: 0 12px 48px 0 rgba(0,0,0,0.4);
    font-weight: 500;
    transition: all 0.3s ease;
}

.record-overlay-box.connecting {
    background: linear-gradient(135deg, rgba(74, 111, 165, 0.95) 70%, rgba(46, 74, 107, 0.85));
}

.record-overlay-box.recording {
    background: linear-gradient(135deg, rgba(49, 68, 91, 0.95) 80%, rgba(20, 29, 41, 0.85));
}
.overlay-mic {
    width: 56px;
    height: 56px;
    filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.3));
    transition: all 0.3s ease;
}

.overlay-mic.pulse {
    animation: pulse-mic 1.5s ease-in-out infinite;
}

@keyframes pulse-mic {
    0%, 100% {
        transform: scale(1);
        filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.3));
    }
    50% {
        transform: scale(1.1);
        filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.5));
    }
}

.overlay-text {
    text-align: center;
    width: 100%;
}

/* 连接中状态 */
.connecting-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.connecting-title {
    font-size: 22px;
    font-weight: 600;
    color: #fff;
}

.connecting-subtitle {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
}

.connecting-progress {
    margin-top: 4px;
}

.progress-dots {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.progress-dots span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    animation: dot-pulse 1.4s ease-in-out infinite;
}

.progress-dots span:nth-child(2) {
    animation-delay: 0.2s;
}

.progress-dots span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes dot-pulse {
    0%, 60%, 100% {
        opacity: 0.3;
        transform: scale(0.8);
    }
    30% {
        opacity: 1;
        transform: scale(1.2);
    }
}

/* 录音中状态 */
.recording-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.recording-title {
    font-size: 22px;
    font-weight: 600;
    color: #A6F55D;
}

.recording-subtitle {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.9);
}

/* 就绪状态 */
.ready-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.ready-title {
    font-size: 22px;
    font-weight: 600;
    color: #A6F55D;
}

.ready-subtitle {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.7;
    color: inherit;
    letter-spacing: 2px;
}
@keyframes fadeInOverlay {
    from { opacity: 0; }
    to { opacity: 1; }
}

.connection-progress {
    position: absolute;
    top: -40px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
}

.progress-bar {
    width: 120px;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 4px;
}

.progress-fill {
    height: 100%;
    background: #4CAF50;
    border-radius: 2px;
    transition: width 0.3s ease;
}

.progress-text {
    text-align: center;
    font-size: 10px;
}

@keyframes pulse-recording {
    0% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
    }
    50% { 
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(244, 67, 54, 0.5);
    }
    100% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
    }
}

@keyframes pulse-connecting {
    0% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    }
    50% { 
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(255, 152, 0, 0.5);
    }
    100% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    }
}

@keyframes pulse-icon {
    0% { 
        transform: scale(1);
        opacity: 1;
    }
    50% { 
        transform: scale(1.1);
        opacity: 0.8;
    }
    100% { 
        transform: scale(1);
        opacity: 1;
    }
}
</style>
